import React, { useEffect, useMemo, useState } from 'react';
import { resolveImageUrl } from '../../utils/image';
import {
  Alert,
  Avatar,
  Box,
  Button as MuiButton,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { ROUTES } from '../../constants';
import { fetchCategories } from '../../features/categoriesSlice';
import { fetchProducts } from '../../features/productsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Product } from '../../types';
import { getEffectivePrice, hasDiscount } from '../../utils/pricing';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import PriceDisplay from '../../components/common/PriceDisplay';

type SortKey = 'name' | 'price' | 'stock' | 'updatedAt';

const AdminProductsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, loading, currentPage, totalPages } = useAppSelector((state) => state.products);
  const { items: categories } = useAppSelector((state) => state.categories);

  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [sortBy, setSortBy] = useState<SortKey>('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [inlineDrafts, setInlineDrafts] = useState<
    Record<string, { price: string; stock: string }>
  >({});

  // "Run a sale" bulk tool
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [saleScope, setSaleScope] = useState<'all' | 'category'>('all');
  const [saleCategoryId, setSaleCategoryId] = useState('');
  const [salePercent, setSalePercent] = useState('10');
  const [saleStartsAt, setSaleStartsAt] = useState('');
  const [saleEndsAt, setSaleEndsAt] = useState('');
  const [saleBusy, setSaleBusy] = useState(false);
  const [saleError, setSaleError] = useState<string | null>(null);
  const [saleResult, setSaleResult] = useState<string | null>(null);

  const submitSale = async (action: 'start' | 'clear') => {
    setSaleBusy(true);
    setSaleError(null);
    try {
      const scopeBody = {
        scope: saleScope,
        ...(saleScope === 'category' ? { categoryId: saleCategoryId } : {})
      };
      if (action === 'start') {
        const response = await apiClient.post('/products/sale', {
          ...scopeBody,
          percent: Number(salePercent),
          startsAt: saleStartsAt ? new Date(saleStartsAt).toISOString() : null,
          endsAt: saleEndsAt ? new Date(saleEndsAt).toISOString() : null
        });
        setSaleResult(
          `Sale applied to ${response.data.updated} product${response.data.updated === 1 ? '' : 's'} — ${salePercent}% off${saleEndsAt ? `, ends ${new Date(saleEndsAt).toLocaleString('en-IN')}` : ''}.`
        );
      } else {
        const response = await apiClient.post('/products/sale/clear', scopeBody);
        setSaleResult(
          `Sale ended — promo pricing removed from ${response.data.updated} product${response.data.updated === 1 ? '' : 's'}.`
        );
      }
      setSaleDialogOpen(false);
      loadProducts();
    } catch (err) {
      setSaleError(extractApiErrorMessage(err, 'Could not update the sale'));
    } finally {
      setSaleBusy(false);
    }
  };

  const loadProducts = (nextPage = page, nextCategory = categoryFilter) =>
    dispatch(
      fetchProducts({
        categoryId: nextCategory || undefined,
        page: nextPage,
        limit: 20
      })
    );

  useEffect(() => {
    loadProducts(1);
    dispatch(fetchCategories());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const categoryFromQuery = searchParams.get('category');
    if (categoryFromQuery) {
      setCategoryFilter(categoryFromQuery);
      setPage(1);
      loadProducts(1, categoryFromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    loadProducts(page, categoryFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const visibleProducts = useMemo(() => {
    let list = items.filter((p) => {
      const q = query.trim().toLowerCase();
      const queryMatch =
        q === '' ||
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        p.category?.name?.toLowerCase().includes(q);
      const categoryMatch = !categoryFilter || p.category?._id === categoryFilter;
      const statusMatch =
        statusFilter === 'all' ||
        (statusFilter === 'active' && p.isActive) ||
        (statusFilter === 'inactive' && !p.isActive);
      return queryMatch && categoryMatch && statusMatch;
    });

    list = [...list].sort((a, b) => {
      if (sortBy === 'name') {
        return sortDir === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (sortBy === 'price') {
        return sortDir === 'asc'
          ? getEffectivePrice(a) - getEffectivePrice(b)
          : getEffectivePrice(b) - getEffectivePrice(a);
      }
      if (sortBy === 'stock') return sortDir === 'asc' ? a.stock - b.stock : b.stock - a.stock;
      const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return sortDir === 'asc' ? aDate - bDate : bDate - aDate;
    });

    return list;
  }, [items, query, categoryFilter, statusFilter, sortBy, sortDir]);

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected]
  );

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDir('asc');
  };

  const setInlineValue = (id: string, key: 'price' | 'stock', value: string) => {
    setInlineDrafts((prev) => ({
      ...prev,
      [id]: {
        price: prev[id]?.price ?? '',
        stock: prev[id]?.stock ?? '',
        [key]: value
      }
    }));
  };

  const saveInline = async (product: Product) => {
    const draft = inlineDrafts[product._id];
    if (!draft) return;
    const payload: { price?: number; stock?: number } = {};
    if (draft.price !== '') payload.price = Number(draft.price);
    if (draft.stock !== '') payload.stock = Number(draft.stock);
    if (Object.keys(payload).length === 0) return;
    await apiClient.put(`/products/${product._id}`, payload);
    loadProducts();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Operational catalog management with fast edits and bulk actions.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <MuiButton variant="outlined" color="secondary" onClick={() => setSaleDialogOpen(true)}>
            Run a sale
          </MuiButton>
          <MuiButton variant="contained" onClick={() => navigate(`${ROUTES.ADMIN}/products/new`)}>
            Add product
          </MuiButton>
        </Box>
      </Box>

      {saleResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaleResult(null)}>
          {saleResult}
        </Alert>
      )}

      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          bgcolor: '#f9fafb',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 220px 180px' },
          gap: 1.5
        }}
      >
        <TextField
          size="small"
          placeholder="Search by name, SKU, or category"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <TextField
          select
          size="small"
          value={categoryFilter}
          onChange={(e) => {
            const nextCategory = e.target.value;
            setCategoryFilter(nextCategory);
            setPage(1);
            loadProducts(1, nextCategory);
          }}
        >
          <MenuItem value="">All categories</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c._id} value={c._id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
          }
        >
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </Box>

      {selectedIds.length > 0 && (
        <Box
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 2,
            bgcolor: '#f9fafb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="body2">{selectedIds.length} selected</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <MuiButton
              size="small"
              onClick={async () => {
                await Promise.all(
                  selectedIds.map((id) => apiClient.put(`/products/${id}`, { isActive: false }))
                );
                setSelected({});
                loadProducts();
              }}
            >
              Deactivate
            </MuiButton>
            <MuiButton
              size="small"
              onClick={async () => {
                await Promise.all(
                  selectedIds.map((id) => apiClient.put(`/products/${id}`, { isActive: true }))
                );
                setSelected({});
                loadProducts();
              }}
            >
              Activate
            </MuiButton>
            <MuiButton size="small" onClick={() => setSelected({})}>
              Clear
            </MuiButton>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Skeleton variant="rounded" height={46} />
          <Skeleton variant="rounded" height={46} />
          <Skeleton variant="rounded" height={46} />
          <Skeleton variant="rounded" height={46} />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    visibleProducts.length > 0 && selectedIds.length === visibleProducts.length
                  }
                  indeterminate={
                    selectedIds.length > 0 && selectedIds.length < visibleProducts.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      const all: Record<string, boolean> = {};
                      visibleProducts.forEach((p) => {
                        all[p._id] = true;
                      });
                      setSelected(all);
                    } else {
                      setSelected({});
                    }
                  }}
                />
              </TableCell>
              <TableCell>Thumbnail</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortDir : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'price'}
                  direction={sortBy === 'price' ? sortDir : 'asc'}
                  onClick={() => handleSort('price')}
                >
                  Price
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'stock'}
                  direction={sortBy === 'stock' ? sortDir : 'asc'}
                  onClick={() => handleSort('stock')}
                >
                  Stock
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'updatedAt'}
                  direction={sortBy === 'updatedAt' ? sortDir : 'desc'}
                  onClick={() => handleSort('updatedAt')}
                >
                  Updated
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleProducts.map((product) => (
              <TableRow
                key={product._id}
                hover
                onClick={() => navigate(`${ROUTES.ADMIN}/products/${product._id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={Boolean(selected[product._id])}
                    onChange={(e) =>
                      setSelected((prev) => ({ ...prev, [product._id]: e.target.checked }))
                    }
                  />
                </TableCell>
                <TableCell>
                  <Avatar
                    src={resolveImageUrl(product.images?.[0] || '')}
                    variant="rounded"
                    sx={{ width: 34, height: 34 }}
                  />
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.sku}</TableCell>
                <TableCell>{product.category?.name || '-'}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Box sx={{ display: 'grid', gap: 0.5 }}>
                    <TextField
                      size="small"
                      value={inlineDrafts[product._id]?.price ?? getEffectivePrice(product)}
                      onChange={(e) => setInlineValue(product._id, 'price', e.target.value)}
                      onBlur={() => saveInline(product)}
                      sx={{ maxWidth: 92 }}
                    />
                    {hasDiscount(product) && (
                      <PriceDisplay
                        item={product}
                        showSalePrice={false}
                        mrpVariant="caption"
                        discountChipSize="small"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <TextField
                    size="small"
                    value={inlineDrafts[product._id]?.stock ?? product.stock}
                    onChange={(e) => setInlineValue(product._id, 'stock', e.target.value)}
                    onBlur={() => saveInline(product)}
                    sx={{ maxWidth: 72 }}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={product.isActive ? 'Visible in storefront' : 'Hidden'}>
                    <Chip
                      size="small"
                      label={product.isActive ? 'Active' : 'Inactive'}
                      color={product.isActive ? 'success' : 'default'}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {product.updatedAt ? new Date(product.updatedAt).toLocaleDateString() : '-'}
                </TableCell>
              </TableRow>
            ))}
            {visibleProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No products match your filters.
                    </Typography>
                    <MuiButton sx={{ mt: 1 }} onClick={() => setQuery('')}>
                      Clear search
                    </MuiButton>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            page={currentPage}
            count={totalPages}
            color="primary"
            onChange={(_, nextPage) => setPage(nextPage)}
          />
        </Box>
      )}

      <Dialog open={saleDialogOpen} onClose={() => !saleBusy && setSaleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Run a sale</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Puts a percent-off promo price on every product in scope. Prices revert automatically
            when the sale ends — or end it early from here.
          </Typography>
          {saleError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaleError(null)}>
              {saleError}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              select
              size="small"
              label="Applies to"
              value={saleScope}
              onChange={(e) => setSaleScope(e.target.value as 'all' | 'category')}
            >
              <MenuItem value="all">All products</MenuItem>
              <MenuItem value="category">One category</MenuItem>
            </TextField>
            {saleScope === 'category' && (
              <TextField
                select
                size="small"
                label="Category"
                value={saleCategoryId}
                onChange={(e) => setSaleCategoryId(e.target.value)}
              >
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              size="small"
              type="number"
              label="Discount (%)"
              value={salePercent}
              onChange={(e) => setSalePercent(e.target.value)}
              inputProps={{ min: 1, max: 90 }}
              helperText="Off the current selling price; MRP strike-through stays"
            />
            <TextField
              size="small"
              type="datetime-local"
              label="Starts (optional)"
              value={saleStartsAt}
              onChange={(e) => setSaleStartsAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Empty = starts immediately"
            />
            <TextField
              size="small"
              type="datetime-local"
              label="Ends (optional)"
              value={saleEndsAt}
              onChange={(e) => setSaleEndsAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Empty = runs until you end it"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <MuiButton
            color="error"
            disabled={saleBusy || (saleScope === 'category' && !saleCategoryId)}
            onClick={() => submitSale('clear')}
          >
            End sale now
          </MuiButton>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <MuiButton disabled={saleBusy} onClick={() => setSaleDialogOpen(false)}>
              Cancel
            </MuiButton>
            <MuiButton
              variant="contained"
              disabled={
                saleBusy ||
                !(Number(salePercent) >= 1 && Number(salePercent) <= 90) ||
                (saleScope === 'category' && !saleCategoryId)
              }
              onClick={() => submitSale('start')}
            >
              {saleBusy ? 'Applying…' : 'Start sale'}
            </MuiButton>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminProductsPage;
